import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ProIcon } from '../components/ProIcon';
import { COMPANY_INFO, LEGAL_LAST_UPDATED } from '../config/company';
import { trackContactClick } from '../lib/analytics';
import { ObfuscatedEmail } from '../components/ObfuscatedEmail';


// Trust signals for booking confidence
const trustSignals = [
  { icon: 'shield-check', value: 'Secure', label: 'Payments' },
  { icon: 'calendar-check', value: '60+ Day', label: 'Full Refund' },
  { icon: 'headset', value: '24/7', label: 'Local Contact' },
  { icon: 'file-check', value: 'Clear', label: 'Terms' },
];

// Core booking terms as service cards
const bookingTerms = [
  {
    id: 'contracting-party',
    icon: 'building',
    title: 'Contracting Party',
    description: `Direct bookings are made with ${COMPANY_INFO.legalName}, trading as ${COMPANY_INFO.brandName}. Where we manage a home on behalf of an owner, we remain your booking contact and in-stay point of support unless your confirmation expressly says otherwise.`,
  },
  {
    id: 'reservation',
    icon: 'file-lines',
    title: 'Reservation & Payment',
    description: 'A reservation is confirmed only once we issue written confirmation and receive the agreed payment. Your confirmation records the property reference, stay dates, guest numbers, total price, payment schedule, and any approved extras or property-specific conditions.',
  },
  {
    id: 'deposit',
    icon: 'wallet',
    title: 'Damage Deposit',
    description: 'Where a damage deposit applies, the amount, collection method, and return process are confirmed before booking. The deposit may be used for damage, missing items, extra cleaning, call-outs, and any other reasonable cost, charge, loss, or expense arising from the guest stay or a breach of these terms. Any unused balance is normally returned within 7 working days of departure once checks are complete.',
  },
  {
    id: 'cancellations',
    icon: 'calendar-xmark',
    title: 'Cancellations',
    description: 'By default across all homes, cancellations made 60 days or more before arrival receive a full refund of sums paid for accommodation. For cancellations made fewer than 60 days before arrival, the applicable cancellation charge follows the payment terms agreed for the booking. If we cancel because the reserved property becomes unavailable and you do not accept a suitable alternative, we refund the sums paid for accommodation. No cancellation penalty applies where cancellation results solely from force majeure that is properly evidenced.',
  },
  {
    id: 'checkin',
    icon: 'door-open',
    title: 'Check-In & Out',
    description: 'Standard check-in is from 16:00 and check-out is by 10:00, unless we agree otherwise in writing. Please share accurate arrival details in advance so handover can be arranged smoothly. Late check-in or special handover charges, where applicable, are disclosed before booking or in the confirmation. Late check-out is subject to availability and can never be guaranteed.',
  },
  {
    id: 'guest-id',
    icon: 'id-card',
    title: 'Guest ID & Registration',
    description: 'All guests must provide valid identification before or at check-in. As required by Spanish law, we may need to collect, record, and submit traveller information to the relevant authorities. Failure to provide the required identification may delay or prevent check-in where the legal requirement has been explained in advance.',
  },
  {
    id: 'extra-charges',
    icon: 'key',
    title: 'Guest-Caused Charges',
    description: 'Certain costs only arise if an issue occurs during or after the stay. These may include replacement keys, lockouts, extra cleaning, emergency call-outs, unauthorised occupancy, smoking inside, unauthorised pets, damage, or other guest-caused costs. Where applicable, such sums may be deducted from the damage deposit or invoiced separately.',
  },
  {
    id: 'utilities',
    icon: 'bolt',
    title: 'Included Services',
    description: 'The booking price includes the accommodation and the standard services listed in your confirmation. Utilities are included unless a separate charging basis was disclosed before booking. Optional extras agreed before arrival, such as pool heating, baby equipment, transfers, or housekeeping, are charged at the quoted rate.',
  },
  {
    id: 'changes',
    icon: 'arrows-rotate',
    title: 'Changes, No-Show & Early Departure',
    description: 'Date changes, guest-name changes, or other amendments depend on availability and may involve a rate difference or administration charge. If guests do not arrive and do not remain in contact within the agreed arrival window, the booking may be treated as a no-show. No refund is normally due for unused nights, late arrival, voluntary early departure, or reduced occupancy unless we agree otherwise in writing.',
  },
  {
    id: 'property-use',
    icon: 'house',
    title: 'Use of the Property',
    description: 'The property may only be used for the agreed holiday stay by the confirmed guests. Parties, events, filming, commercial activity, subletting, illegal use, and disruptive conduct are not permitted without written approval. Guests must comply with house guidance, community rules, rubbish disposal instructions, parking restrictions, and local noise regulations.',
  },
  {
    id: 'access',
    icon: 'screwdriver-wrench',
    title: 'Maintenance & Access',
    description: 'We and our contractors may need reasonable access for urgent repairs, safety checks, pool service, gardening, or legally required inspections. Wherever possible, we will keep any disruption to a minimum. Temporary interruptions to utilities, internet, lifts, community services, or public infrastructure can occur and do not automatically entitle the guest to compensation where the issue is outside our reasonable control.',
  },
  {
    id: 'accuracy',
    icon: 'bullseye',
    title: 'Property Details',
    description: 'We aim to keep descriptions, photography, inventories, and amenity information accurate. Small layout, furnishing, equipment, or view changes can happen from time to time. If a material feature changes before arrival, we will tell you as soon as reasonably possible and, where appropriate, offer a suitable solution.',
  },
];

// Stay rules
const stayRules = [
  'Only the confirmed guests may stay at the property unless we approve a change in writing.',
  'Treat the property, furniture, fixtures, and contents with proper care throughout the stay, allowing for normal holiday use.',
  'Report breakages, damage, or safety issues immediately so repair costs and disruption do not increase.',
  'Allow reasonable access for inspection or urgent maintenance.',
  'No illegal, dangerous, noisy, offensive, or disruptive activity.',
  'Respect community rules, neighbours, local quiet hours, and any lawful instructions relating to the property.',
  'No smoking inside the property unless a property-specific term expressly allows it.',
  'No parties, events, filming, commercial activity, or subletting without written approval.',
  'Dispose of rubbish as instructed and leave the property reasonably tidy on departure, including basic kitchen washing-up and general order.',
  'Do not tamper with alarms, internet equipment, pool systems, or locked owner areas.',
  'Children must be supervised appropriately, especially around pools, terraces, stairs, and balconies.',
];

// Complaint steps
const complaintSteps = [
  `Please report any issue as soon as reasonably possible during the stay using our 24-hour contact number at ${COMPANY_INFO.phone}.`,
  'If the matter cannot be resolved during the stay, please send the complaint in writing, with any supporting photos or documents, as soon as possible after departure and no later than 7 days after check-out.',
  'Official Andalusian complaint forms are available where required by law. Any request for compensation may be reduced or refused where the issue was not reported promptly and could reasonably have been addressed during the stay.',
];

const legalNotes = [
  'Bookings for accommodation supplied for specific dates do not benefit from the standard 14-day cooling-off period.',
  'These terms are governed by Spanish law.',
  'If any part of these terms is found unenforceable, the rest of the terms continue to apply.',
];

export const BookingTerms = () => {
  return (
    <main className="min-h-screen bg-white pt-24" role="main" aria-label="Booking Terms">
      {/* Hero Section */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16 md:mb-24"
        >
          <p className="ui-eyebrow-brand mb-4">Legal</p>
          <h1 className="ui-page-title mb-6 max-w-4xl mx-auto">
            Booking Terms & Conditions
          </h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Clear terms for carefully managed Marbella stays, so you know what to expect before arrival, during the stay, and at departure.
          </p>
          <p className="text-sm text-slate-500 mt-4">Last updated: {LEGAL_LAST_UPDATED}</p>
        </motion.div>

        {/* Trust Signals Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-10 sm:mb-16"
        >
          {trustSignals.map((signal, idx) => (
            <div
              key={idx}
              className="flex flex-col items-center text-center p-4 sm:p-6 bg-slate-50 rounded-2xl border border-slate-100"
            >
              <ProIcon icon={signal.icon} family="solid" className="text-xl text-brand-ocean mb-2" />
              <span className="text-xl sm:text-2xl font-black text-brand-ocean">{signal.value}</span>
              <span className="text-xs sm:text-sm font-medium text-slate-500">{signal.label}</span>
            </div>
          ))}
        </motion.div>

        {/* Important Notice Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-brand-sand/50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-brand-sand mb-10 sm:mb-16"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-brand-ocean/10 flex items-center justify-center text-brand-ocean shrink-0">
              <ProIcon icon="circle-info" family="solid" className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 mb-2">Important Notice</h2>
              <p className="text-slate-600 leading-relaxed">
                These general terms apply to all direct bookings with {COMPANY_INFO.brandName}, operated by {COMPANY_INFO.legalName}. Before a booking is confirmed, we set out the accommodation price, any advance payment, the cancellation basis, and any approved extras or special conditions. Your confirmation then records the property reference, stay dates, guest numbers, and agreed arrival arrangements.
              </p>
              <div className="mt-4 pt-4 border-t border-brand-sand/80 grid gap-3 sm:grid-cols-2 text-sm text-slate-600">
                <p>
                  <span className="font-semibold text-slate-900">Legal operator:</span>{' '}
                  {COMPANY_INFO.legalName} · Tax ID {COMPANY_INFO.taxId}
                </p>
                <p>
                  <span className="font-semibold text-slate-900">24-hour contact:</span>{' '}
                  <a href={COMPANY_INFO.phoneHref} className="text-brand-ocean hover:underline">
                    {COMPANY_INFO.phone}
                  </a>
                  {' · '}
                  <ObfuscatedEmail className="text-brand-ocean hover:underline" />
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Core Terms Grid */}
        <section
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 mb-10 sm:mb-20"
          aria-label="Core Booking Terms"
        >
          {bookingTerms.map((term, index) => (
            <motion.article
              key={term.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              className="group relative bg-gradient-to-br from-white to-slate-50 border border-slate-200 rounded-2xl sm:rounded-3xl p-5 sm:p-6 hover:shadow-xl hover:shadow-slate-200/50 hover:border-brand-ocean/20 transition-all duration-300"
              aria-labelledby={`term-title-${term.id}`}
            >
              {/* Content */}
              <div className="pt-2">
                {/* Title Row with Icon */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-brand-ocean/10 flex items-center justify-center text-brand-ocean group-hover:bg-brand-ocean group-hover:text-white transition-colors duration-300">
                    <ProIcon icon={term.icon} family="solid" className="w-5 h-5" />
                  </div>
                  <h2
                    id={`term-title-${term.id}`}
                    className="text-base sm:text-lg font-black text-slate-900 tracking-tight"
                  >
                    {term.title}
                  </h2>
                </div>

                {/* Description */}
                <p className="text-slate-600 leading-relaxed text-sm">
                  {term.description}
                </p>
              </div>
            </motion.article>
          ))}
        </section>

        {/* Stay Rules & Complaints - Two Column */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-10 sm:mb-20">
          {/* Guest Obligations */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-br from-white to-slate-50 border border-slate-200 rounded-2xl sm:rounded-3xl p-5 sm:p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-brand-ocean/10 flex items-center justify-center text-brand-ocean">
                <ProIcon icon="clipboard-list" family="solid" className="w-5 h-5" />
              </div>
              <h2 className="text-lg font-black text-slate-900">Guest Obligations</h2>
            </div>
            <ul className="space-y-3">
              {stayRules.map((rule, idx) => (
                <li key={idx} className="flex items-start gap-3 text-sm text-slate-600">
                  <span className="w-5 h-5 rounded-full bg-brand-ocean/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <ProIcon icon="check" family="solid" className="w-3 h-3 text-brand-ocean" />
                  </span>
                  <span>{rule}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Complaints Procedure */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-gradient-to-br from-white to-slate-50 border border-slate-200 rounded-2xl sm:rounded-3xl p-5 sm:p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-brand-ocean/10 flex items-center justify-center text-brand-ocean">
                <ProIcon icon="message-exclamation" family="solid" className="w-5 h-5" />
              </div>
              <h2 className="text-lg font-black text-slate-900">Complaints Procedure</h2>
            </div>
            <ul className="space-y-4">
              {complaintSteps.map((step, idx) => (
                <li key={idx} className="flex items-start gap-3 text-sm text-slate-600">
                  <span className="w-6 h-6 rounded-full bg-brand-ocean/10 flex items-center justify-center flex-shrink-0 text-xs font-bold text-brand-ocean">
                    {idx + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </section>

        {/* Liability Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="mb-10 sm:mb-20"
        >
          <div className="bg-gradient-to-br from-white to-slate-50 border border-slate-200 rounded-2xl sm:rounded-3xl p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-brand-ocean/10 flex items-center justify-center text-brand-ocean">
                <ProIcon icon="scale-balanced" family="solid" className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-black text-slate-900">Liability & Early Departure</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6 text-sm text-slate-600 leading-relaxed">
              <div>
                <p className="mb-3">
                  We are responsible for providing the accommodation and any services we expressly agree to provide with reasonable care and skill. We are not responsible for losses caused by the guest, other members of the party, unrelated third parties, or events outside our reasonable control.
                </p>
                <p className="mb-4">
                  Nothing in these terms limits liability where the law does not allow that limitation. If a problem arises, guests are expected to take reasonable steps to reduce further loss and to cooperate with any practical solution we offer.
                </p>
                <ul className="space-y-2">
                  {legalNotes.map((note) => (
                    <li key={note} className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded-full bg-brand-ocean/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <ProIcon icon="check" family="solid" className="w-3 h-3 text-brand-ocean" />
                      </span>
                      <span>{note}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-brand-sand/30 rounded-xl p-4 border border-brand-sand">
                <p className="font-semibold text-slate-900 mb-2">Right to End Stay Early</p>
                <p>
                  We may end the stay early, without refund, where that outcome is reasonable and proportionate because of a serious or repeated breach. This includes violent or threatening behaviour, unauthorised parties, significant damage, illegal activity, or repeated breach of house or community rules. Where appropriate, guests will first be given a fair opportunity to correct the breach.
                </p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="bg-gradient-to-br from-brand-ocean to-brand-ocean-dark rounded-3xl p-8 sm:p-12 lg:p-16 text-center text-white relative overflow-hidden"
          aria-label="Contact CTA"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
          </div>

          <div className="relative z-10 max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-4 leading-tight">
              Questions Before You Book?
            </h2>
            <p className="text-base sm:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              Need clarification on a property, payment timing, or arrival procedure? Our team is here to help.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              {/* Primary CTA - Live Chat */}
              <button
                onClick={() => {
                  trackContactClick('booking_terms_cta', 'live_chat');
                  if (window.$crisp) window.$crisp.push(['do', 'chat:open']);
                }}
                className="ui-btn-primary w-full sm:w-auto justify-center text-sm sm:text-base px-8 py-4 gap-2 group"
              >
                <ProIcon icon="comments" family="solid" className="w-4 h-4" />
                <span>Chat with us</span>
              </button>

              {/* Secondary CTA - Contact Page */}
              <Link
                to="/contact"
                onClick={() => trackContactClick('booking_terms_cta', 'contact')}
                className="ui-btn-secondary text-sm sm:text-base px-8 py-4 gap-2 group"
              >
                <ProIcon icon="envelope" family="solid" className="w-4 h-4" />
                <span>Contact</span>
              </Link>
            </div>

            {/* Trust Microcopy */}
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-xs sm:text-sm text-white/60">
              <span className="flex items-center gap-1.5">
                <ProIcon icon="clock" family="light" className="w-3.5 h-3.5" />
                Response within 2 hours
              </span>
              <span className="hidden sm:inline">•</span>
              <span className="flex items-center gap-1.5">
                <ProIcon icon="headset" family="light" className="w-3.5 h-3.5" />
                Real local team
              </span>
            </div>
          </div>
        </motion.section>
      </header>
    </main>
  );
};
